import {
  createConnectQueryKey,
  useMutation,
  useQuery,
  useTransport,
} from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import {
  archiveOrganizationEventType,
  createOrganizationEventType,
  getSystemSettings,
  listOrganizationEventTypes,
  reorderOrganizationEventTypes,
  updateOrganizationEventType,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  ListOrganizationEventTypesRequestSchema,
  ListOrganizationEventTypesResponseSchema,
  type ListOrganizationEventTypesResponse,
} from '@/gen/cortado/v1/cortado_pb'
import { authStore } from './auth.store'

/**
 * Hook to fetch an organization's event types.
 *
 * The settings screen manages archived types too, so `includeArchived`
 * defaults to true here. Everywhere else in the app, read event types from
 * `useEventTypes()` (backed by the cached system settings) instead.
 */
export function useOrganizationEventTypes(
  organizationId: string,
  includeArchived = true
) {
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  const query = useQuery(
    listOrganizationEventTypes,
    create(ListOrganizationEventTypesRequestSchema, {
      organizationId,
      includeArchived,
    }),
    {
      enabled: isAuthenticated && !!organizationId,
      select: (data) => data?.eventTypes || [],
    }
  )

  return {
    eventTypes: query.data || [],
    isLoading: query.isLoading,
    hasLoaded: query.isSuccess || !!query.data,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Invalidate every cache that carries event types: the list query backing the
 * settings screen, and the system settings query the rest of the app reads
 * labels/colors/icons from.
 */
function useInvalidateEventTypeCaches() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  return () => {
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listOrganizationEventTypes,
        transport,
        cardinality: undefined,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: getSystemSettings,
        transport,
        cardinality: undefined,
      }),
    })
  }
}

/**
 * Hook to create a new organization event type.
 *
 * The immutable `key` is derived server-side from the label; it is not part of
 * the request.
 */
export function useCreateOrganizationEventType() {
  const invalidate = useInvalidateEventTypeCaches()

  const mutation = useMutation(createOrganizationEventType, {
    onSuccess: invalidate,
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update an existing organization event type.
 *
 * `key` is immutable and cannot be updated.
 */
export function useUpdateOrganizationEventType() {
  const invalidate = useInvalidateEventTypeCaches()

  const mutation = useMutation(updateOrganizationEventType, {
    onSuccess: invalidate,
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to reorder an organization's event types via drag-and-drop.
 *
 * The request carries the full ordered list of active event type IDs; the
 * server renumbers `display_order` to 0..n-1 from array position.
 *
 * This hook:
 * - Optimistically renumbers `displayOrder` in the list caches (no snap-back)
 * - Rolls back on error
 * - Invalidates event type caches on success to sync with the server order
 */
export function useReorderOrganizationEventTypes() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const invalidate = useInvalidateEventTypeCaches()

  const mutation = useMutation(reorderOrganizationEventTypes, {
    onMutate: async (variables) => {
      const listQueryKey = createConnectQueryKey({
        schema: listOrganizationEventTypes,
        transport,
        cardinality: undefined,
      })

      await queryClient.cancelQueries({ queryKey: listQueryKey })

      const previousQueries = queryClient.getQueriesData({
        queryKey: listQueryKey,
      })

      previousQueries.forEach(([key, data]) => {
        const typedData = data as ListOrganizationEventTypesResponse | undefined
        if (!typedData?.eventTypes) return

        const eventTypeIds = variables.eventTypeIds ?? []
        if (!eventTypeIds.length) return

        const orderMap = new Map(eventTypeIds.map((id, index) => [id, index]))
        const reordered = typedData.eventTypes.map((eventType) => {
          const newOrder = orderMap.get(eventType.id)
          // Archived types are not part of the payload and stay unchanged.
          return newOrder !== undefined
            ? { ...eventType, displayOrder: newOrder }
            : eventType
        })

        queryClient.setQueryData(
          key,
          create(ListOrganizationEventTypesResponseSchema, {
            eventTypes: reordered,
          })
        )
      })

      return { previousQueries }
    },

    onError: (_error, _variables, context) => {
      const { previousQueries } = context as {
        previousQueries: Array<
          [unknown[], ListOrganizationEventTypesResponse | undefined]
        >
      }
      previousQueries?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },

    onSuccess: invalidate,
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to archive an organization event type (soft delete).
 *
 * Archiving hides the type from create/edit dropdowns; existing records keep
 * their key and still render and filter.
 */
export function useArchiveOrganizationEventType() {
  const invalidate = useInvalidateEventTypeCaches()

  const mutation = useMutation(archiveOrganizationEventType, {
    onSuccess: invalidate,
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
